import React from "react";
import { Box, Tooltip } from "@mui/material";
import { Label12Regular } from "components";
import { toUSNumber } from "utils";
import { StatusDataApiType, StatusType } from "../../types";
import { StatusBreakdownBarProps } from "./types";
import { STATUS_COLORS } from "./consts";

const StatusBreakdownBar = ({
  data,
  isApprovalFlowOn,
  isAdditionalStatusesOn,
}: StatusBreakdownBarProps) => {
  const statusDataFromApi = data?.billStatusCount || [];

  const statusDataParsed = statusDataFromApi.reduce(
    (
      acc: StatusDataApiType[],
      { status, count, percentage }: StatusDataApiType,
    ) => {
      const actualStatus = status.includes("Not Applicable")
        ? "Not Applicable"
        : status;
      const existing = acc.find((item) => item.status === actualStatus);

      if (existing) {
        return acc.map((item) =>
          item.status === actualStatus
            ? {
                ...item,
                count: item.count + count,
                percentage: item.percentage + percentage,
              }
            : item,
        );
      }

      return [...acc, { status: actualStatus, count, percentage }];
    },
    [],
  );

  const currentStatuses = STATUS_COLORS.filter(
    ({ conditional }) =>
      conditional === undefined ||
      (conditional === "approvalFlow" && isApprovalFlowOn) ||
      (conditional === "additionalStatuses" && isAdditionalStatusesOn),
  );

  const optionsToRender = currentStatuses.map(
    ({ label, color }: StatusType) => {
      const apiEntry = statusDataParsed.find(
        ({ status }: StatusDataApiType) => status === label,
      );
      return {
        status: label,
        count: apiEntry?.count || 0,
        percentage: apiEntry?.percentage || 0,
        color,
      };
    },
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        gap: "16px",
        padding: "10px 16px",
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
      }}
    >
      {optionsToRender.map(
        ({ status: statusLabel, count, percentage, color }) => (
          <Tooltip
            key={`status-${statusLabel}`}
            title={`${toUSNumber(count)} Data ${
              count === 1 ? "Entry" : "Entries"
            }`}
            placement="top"
            arrow
          >
            <Box
              sx={{
                flex: 1,
                minWidth: "220px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: color,
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{ display: "flex", whiteSpace: "nowrap", flexShrink: 0 }}
              >
                <Label12Regular sx={{ fontWeight: 500 }}>
                  {statusLabel}
                </Label12Regular>
              </Box>
              <Box
                data-testid="progress-track"
                sx={{
                  display: "flex",
                  flex: 1,
                  height: "8px",
                  minWidth: "60px",
                  maxWidth: "300px",
                  backgroundColor: "#EAEAEA",
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.min(percentage, 100)}%`,
                    backgroundColor: color,
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  width: "fit-content",
                  whiteSpace: "nowrap",
                }}
              >
                <Label12Regular sx={{ fontWeight: 500 }}>
                  {percentage}%
                </Label12Regular>
              </Box>
            </Box>
          </Tooltip>
        ),
      )}
    </Box>
  );
};

export { StatusBreakdownBar };
